import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconBan,
  IconCalendarOff,
  IconInfoCircle,
  IconPlus,
  IconRefresh,
} from '@tabler/icons-react';
import type { CashierGrant, UserSearchHit } from '@pta/types';
import {
  createCashierGrant,
  fetchCashierGrants,
  revokeCashierGrant,
  searchUsersForGroup,
  updateCashierGrant,
} from '../api/cashier-grants';
import { ApiError } from '../api/client';
import { formatTimestamp } from '../util/format';
import { useAuth } from '../auth/AuthProvider';

/**
 * URL: /app/cashier-grants
 *
 * Pta_leader (saját csoportján) vagy felsőbb admin időszakosan kijelölhet
 * pta_cashier-eket. Saját magát is grantelheti.
 */
export function CashierGrantsPage() {
  const { activeGroupId, myGroups } = useAuth();
  const activeGroup = useMemo(
    () => myGroups.find((g) => g.id === activeGroupId) ?? null,
    [myGroups, activeGroupId],
  );

  const [grants, setGrants] = useState<CashierGrant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure();

  const load = async () => {
    if (activeGroupId === null) return;
    setLoading(true);
    setError(null);
    try {
      setGrants(await fetchCashierGrants(activeGroupId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [activeGroupId]);

  if (activeGroupId === null) {
    return (
      <Stack gap="md">
        <Title order={2}>Pénztárosok</Title>
        <Alert color="yellow" icon={<IconInfoCircle size={18} />}>
          Nincs aktív csoport kiválasztva.
        </Alert>
      </Stack>
    );
  }

  const handleRevoke = (g: CashierGrant) => {
    modals.openConfirmModal({
      title: 'Pénztárosi jog azonnali visszavonása',
      children: (
        <Stack gap="xs">
          <Text>Biztosan visszavonod a pénztárosi jogot ettől a usertől?</Text>
          <Text fw={500}>{g.userDisplayName} ({g.userEmail})</Text>
        </Stack>
      ),
      labels: { confirm: 'Visszavonás', cancel: 'Mégse' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await revokeCashierGrant(g.id);
          notifications.show({ color: 'green', message: 'Visszavonva.' });
          await load();
        } catch (e) {
          notifications.show({
            color: 'red',
            message: e instanceof ApiError ? e.message : String(e),
          });
        }
      },
    });
  };

  const handleClearValidTo = async (g: CashierGrant) => {
    try {
      await updateCashierGrant(g.id, { validTo: null });
      notifications.show({
        color: 'green',
        message: 'Lejárati idő törölve (nyitott).',
      });
      await load();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    }
  };

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={loading} />

      <Group justify="space-between">
        <Title order={2}>
          {activeGroup?.name ?? `#${activeGroupId}`} — Pénztárosok
        </Title>
        <Group>
          <Tooltip label="Frissítés">
            <ActionIcon onClick={() => void load()} variant="light">
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Új pénztáros
          </Button>
        </Group>
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      <Card withBorder p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>User</Table.Th>
              <Table.Th>Érvényes-tól</Table.Th>
              <Table.Th>Érvényes-ig</Table.Th>
              <Table.Th>Állapot</Table.Th>
              <Table.Th style={{ width: 130 }}>Műveletek</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {grants.length === 0 && !loading && (
              <Table.Tr>
                <Table.Td colSpan={5} align="center">
                  Nincs kijelölt pénztáros.
                </Table.Td>
              </Table.Tr>
            )}
            {grants.map((g) => {
              let stateBadge;
              if (g.isExpired) {
                stateBadge = <Badge color="gray">Lejárt</Badge>;
              } else if (g.isFuture) {
                stateBadge = <Badge color="yellow">Jövőbeli</Badge>;
              } else {
                stateBadge = <Badge color="green">Aktív</Badge>;
              }
              return (
                <Table.Tr key={g.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text fw={500}>{g.userDisplayName}</Text>
                      <Text size="xs" c="dimmed">
                        {g.userEmail}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>{formatTimestamp(g.validFrom)}</Table.Td>
                  <Table.Td>
                    {g.validTo ? formatTimestamp(g.validTo) : <em>nyitott</em>}
                  </Table.Td>
                  <Table.Td>{stateBadge}</Table.Td>
                  <Table.Td>
                    {!g.isExpired && (
                      <Group gap={4}>
                        {g.validTo && (
                          <Tooltip label="Lejárati idő törlése (nyitottá tesz)">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => void handleClearValidTo(g)}
                            >
                              <IconCalendarOff size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <Tooltip label="Azonnali visszavonás">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleRevoke(g)}
                          >
                            <IconBan size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>

      <CreateGrantModal
        opened={createOpened}
        onClose={closeCreate}
        groupId={activeGroupId}
        onSaved={async () => {
          closeCreate();
          await load();
        }}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Új grant modal
// ---------------------------------------------------------------------------

interface CreateGrantModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  onSaved: () => void;
}

function CreateGrantModal({
  opened,
  onClose,
  groupId,
  onSaved,
}: CreateGrantModalProps) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<UserSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [validFrom, setValidFrom] = useState<Date | null>(null);
  const [validTo, setValidTo] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset open-kor.
  useEffect(() => {
    if (opened) {
      setQuery('');
      setHits([]);
      setSelectedUserId(null);
      setValidFrom(null);
      setValidTo(null);
    }
  }, [opened]);

  // Debounce-olt user-keresés.
  useEffect(() => {
    if (!opened) return;
    if (query.trim().length < 2) {
      setHits([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchUsersForGroup(groupId, query.trim());
        setHits(results);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, groupId, opened]);

  const data = hits.map((u) => ({
    value: String(u.id),
    label: `${u.displayName} (${u.email})`,
  }));

  const canSave = !!selectedUserId && !saving;

  const handleSave = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    try {
      await createCashierGrant(groupId, {
        userId: Number(selectedUserId),
        validFrom: validFrom ? validFrom.toISOString() : null,
        validTo: validTo ? validTo.toISOString() : null,
      });
      notifications.show({ color: 'green', message: 'Pénztáros kijelölve.' });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Új pénztáros kijelölése"
      size="md"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          A pénztáros adott időszakra rögzíthet befizetéseket és kiadásokat
          ezen a csoporton. A keresés a csoporthoz vagy az intézményéhez
          tartozó user-eket találja meg.
        </Alert>

        <Select
          label="User"
          placeholder="Keresés (email vagy név)…"
          data={data}
          searchable
          searchValue={query}
          onSearchChange={setQuery}
          value={selectedUserId}
          onChange={setSelectedUserId}
          rightSection={searching ? <Text size="xs">…</Text> : null}
          nothingFoundMessage={
            query.trim().length < 2
              ? 'Írj be legalább 2 karaktert'
              : 'Nincs találat'
          }
          required
        />

        <DateTimePicker
          label="Érvényes-tól (opcionális, default: most)"
          value={validFrom}
          onChange={(v) => setValidFrom(v ? new Date(v) : null)}
          clearable
        />

        <DateTimePicker
          label="Érvényes-ig (opcionális, default: nyitott)"
          value={validTo}
          onChange={(v) => setValidTo(v ? new Date(v) : null)}
          clearable
        />

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Kijelölés
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
